import { useQuery } from "@apollo/client";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Typography,
} from "@mui/material";
import { useSnackbar } from "notistack";
import { ReactElement } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { z } from "zod";

import {
  ArchivePricingBundleMutation,
  ArchivePricingBundleMutationVariables,
  GetActivePricingBundlesQuery,
  GetPricingBundleArchiveImpactQuery,
  GetPricingBundleArchiveImpactQueryVariables,
} from "~frontend/__generated__/graphql";
import { AsyncWrapper } from "~frontend/components/AsyncWrapper";
import { FlexBox } from "~frontend/components/FlexBox";
import {
  RhfErrorBanner,
  RhfSelect,
} from "~frontend/components/forms/react-hook-form-components";
import ChevronDown from "~frontend/components/icons/ChevronDown";
import {
  ARCHIVE_PRICING_BUNDLE,
  GET_ACTIVE_PRICING_BUNDLES,
  GET_PRICING_BUNDLE_ARCHIVE_IMPACT,
  LIST_ALL_ACTIVE_PRICING_ASSIGNMENTS,
} from "~frontend/graphql/pricingBundle";
import { BundlePricePreview } from "~frontend/routes/organization/pricing/ChangePricingDialog";
import { palette } from "~frontend/theme";
import { formatDateUTC } from "~frontend/util/dateFormat";
import { useErrorMessageFormatter } from "~frontend/util/errors";
import { plural } from "~frontend/util/plural";
import useMutation from "~frontend/util/useMutation";

type Bundle = GetActivePricingBundlesQuery["getActivePricingBundles"][number];
type ArchiveImpact =
  GetPricingBundleArchiveImpactQuery["getPricingBundleArchiveImpact"];

type FormValues = {
  replacementBundleId: string;
};

function makeSchema(replacementRequired: boolean) {
  return z
    .object({
      replacementBundleId: z.string(),
    })
    .superRefine((data, ctx) => {
      if (replacementRequired && !data.replacementBundleId) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: "Required",
          path: ["replacementBundleId"],
        });
      }
    });
}

export function ArchivePricingBundleDialog({
  open,
  onClose,
  bundleId,
  bundleName,
  bundles,
}: {
  open: boolean;
  onClose: () => void;
  bundleId: string;
  bundleName: string;
  bundles: Bundle[];
}): ReactElement {
  const impactQuery = useQuery<
    GetPricingBundleArchiveImpactQuery,
    GetPricingBundleArchiveImpactQueryVariables
  >(GET_PRICING_BUNDLE_ARCHIVE_IMPACT, {
    variables: { input: { pricingBundleId: bundleId } },
    fetchPolicy: "network-only",
    pollInterval: 30000, // Poll in case of changes while dialog is open
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Archive{" "}
        <Box component="span" sx={{ fontWeight: "bold" }}>
          {bundleName}
        </Box>
      </DialogTitle>
      <AsyncWrapper
        data={impactQuery.data}
        loading={impactQuery.loading}
        errors={impactQuery.error}
      >
        {data => (
          <ArchiveDialogBody
            impact={data.getPricingBundleArchiveImpact}
            bundleId={bundleId}
            bundleName={bundleName}
            bundles={bundles}
            onClose={onClose}
          />
        )}
      </AsyncWrapper>
    </Dialog>
  );
}

function ArchiveDialogBody({
  impact,
  bundleId,
  bundleName,
  bundles,
  onClose,
}: {
  impact: ArchiveImpact;
  bundleId: string;
  bundleName: string;
  bundles: Bundle[];
  onClose: () => void;
}): ReactElement {
  const { enqueueSnackbar } = useSnackbar();
  const formatErrorMessage = useErrorMessageFormatter();

  const hasActiveAssignments =
    impact.activeTenantAssignments.length > 0 ||
    impact.activeSubaccountAssignments.length > 0;
  const hasScheduledAssignments =
    impact.scheduledTenantAssignments.length > 0 ||
    impact.scheduledSubaccountAssignments.length > 0;
  const hasAnyImpact = hasActiveAssignments || hasScheduledAssignments;
  const requiresReplacement = hasAnyImpact;

  const replacementOptions = bundles.filter(
    b => b.id !== bundleId && !b.isSingleUse
  );

  const methods = useForm<FormValues>({
    resolver: zodResolver(makeSchema(requiresReplacement)),
    defaultValues: { replacementBundleId: "" },
    mode: "onSubmit",
  });
  const { control, handleSubmit, setError, watch } = methods;
  const selectedReplacementBundleId = watch("replacementBundleId");
  const selectedReplacementBundle = selectedReplacementBundleId
    ? bundles.find(b => b.id === selectedReplacementBundleId)
    : undefined;

  const [archiveBundle, { loading: submitting }] = useMutation<
    ArchivePricingBundleMutation,
    ArchivePricingBundleMutationVariables
  >(ARCHIVE_PRICING_BUNDLE, {
    onErrorSnackbarMessage: "Failed to archive bundle",
    refetchQueries: [
      GET_ACTIVE_PRICING_BUNDLES,
      LIST_ALL_ACTIVE_PRICING_ASSIGNMENTS,
    ],
    awaitRefetchQueries: true,
  });

  async function onSubmit(values: FormValues) {
    if (requiresReplacement && !values.replacementBundleId) {
      setError("replacementBundleId", { message: "Required" });
      return;
    }

    const { errors } = await archiveBundle({
      variables: {
        input: {
          pricingBundleId: bundleId,
          replacementBundleId: requiresReplacement
            ? values.replacementBundleId
            : null,
        },
      },
    });
    if (errors) {
      setError("root", { message: formatErrorMessage(errors) });
      return;
    }
    enqueueSnackbar(`Bundle "${bundleName}" archived`, { variant: "success" });
    onClose();
  }

  return (
    <FormProvider {...methods}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogContent sx={{ pt: 0 }}>
          <Box>
            <RhfErrorBanner control={control} />

            {!hasAnyImpact && (
              <Typography variant="body1">
                This bundle has no active or scheduled assignments. Archive it
                now?
              </Typography>
            )}
          </Box>

          <FlexBox direction="column" gap={3}>
            {hasActiveAssignments && (
              <FlexBox direction="column" gap={1}>
                <Typography variant="body1">
                  This pricing option is currently being used by{" "}
                  <strong>
                    {plural(
                      impact.activeTenantAssignments.length,
                      "tenant",
                      "tenants"
                    )}{" "}
                    and{" "}
                    {plural(
                      impact.activeSubaccountAssignments.length,
                      "subaccount",
                      "subaccounts"
                    )}
                  </strong>
                  . In order to archive this pricing option, you must reassign a
                  new pricing option to all impacted tenants and subaccounts.
                </Typography>
                <AssignmentsAccordion
                  title={`Tenants Using ${bundleName}`}
                  items={[
                    ...impact.activeTenantAssignments.map(a => ({
                      id: a.id,
                      label: a.tenantName,
                    })),
                    ...impact.activeSubaccountAssignments.map(a => ({
                      id: a.id,
                      label: `[${a.tenantName}] Subaccount: ${a.subaccountName}`,
                    })),
                  ]}
                />
              </FlexBox>
            )}

            {hasScheduledAssignments && (
              <FlexBox direction="column" gap={1}>
                <Typography variant="body1">
                  This pricing option is scheduled to be used by{" "}
                  <strong>
                    {plural(
                      impact.scheduledTenantAssignments.length,
                      "tenant",
                      "tenants"
                    )}{" "}
                    and{" "}
                    {plural(
                      impact.scheduledSubaccountAssignments.length,
                      "subaccount",
                      "subaccounts"
                    )}
                  </strong>
                  . Scheduled tenant and subaccount changes will be reassigned
                  to the replacement chosen.
                </Typography>
                <AssignmentsAccordion
                  title={`Tenants Scheduled to Use ${bundleName}`}
                  items={[
                    ...impact.scheduledTenantAssignments.map(a => ({
                      id: a.id,
                      label: `${a.tenantName} (Effective ${formatDateUTC(a.effectiveDate)})`,
                    })),
                    ...impact.scheduledSubaccountAssignments.map(a => ({
                      id: a.id,
                      label: `[${a.tenantName}] Subaccount: ${a.subaccountName} (Effective ${formatDateUTC(a.effectiveDate)})`,
                    })),
                  ]}
                />
              </FlexBox>
            )}

            <FlexBox direction="column">
              {requiresReplacement && (
                <RhfSelect
                  control={control}
                  name="replacementBundleId"
                  label="Reassigned Pricing Option"
                  fullWidth
                >
                  {replacementOptions.map(b => (
                    <MenuItem key={b.id} value={b.id}>
                      {b.name}
                    </MenuItem>
                  ))}
                </RhfSelect>
              )}

              {selectedReplacementBundle && (
                <BundlePricePreview bundle={selectedReplacementBundle} />
              )}
            </FlexBox>
          </FlexBox>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose} disabled={submitting}>
            Cancel
          </Button>
          <Button type="submit" variant="contained" disabled={submitting}>
            Archive {requiresReplacement ? "and Reassign" : ""}
          </Button>
        </DialogActions>
      </form>
    </FormProvider>
  );
}

function AssignmentsAccordion({
  title,
  items,
}: {
  title: string;
  items: { id: string; label: string }[];
}): ReactElement {
  return (
    <Accordion
      disableGutters
      defaultExpanded
      sx={{
        padding: 0,
        background: "none",
        border: `1px solid ${palette.lightGray}`,
      }}
    >
      <AccordionSummary
        expandIcon={<ChevronDown sx={{ fontSize: "1em" }} />}
        sx={{
          backgroundColor: palette.lightGray,
          minHeight: "27px",
          ".MuiAccordionSummary-content": { margin: 0 },
        }}
      >
        <Typography variant="body2Bold">{title}</Typography>
      </AccordionSummary>
      <AccordionDetails sx={{ p: 0 }}>
        <List dense sx={{ px: 2, maxHeight: 200, overflowY: "auto" }}>
          {items.map((item, i) => (
            <ListItem
              key={item.id}
              disableGutters
              divider={i < items.length - 1}
            >
              <ListItemText primary={item.label} />
            </ListItem>
          ))}
        </List>
      </AccordionDetails>
    </Accordion>
  );
}
